import {
  DynamicWidthContainer,
  FlexHorizontalContainer,
} from "@/react/features/styled/WidthContainers";
import { PositionedLabel } from "@/react/features/styled/PositionedLabel";
import Input from "@/react/components/Input";
import Button from "@8x8/oxygen-button";
import styled from "styled-components";

const OuterContainer = styled(DynamicWidthContainer)`
  display: flex;
  flex-direction: column;
`;

const InnerContainer = styled(FlexHorizontalContainer)`
  gap: 10px;
  align-content: center;
  justify-content: center;
`;

/**
 * A labeled input field with an optional button. This makes use of the `PositionedLabel` and `Input` components.
 * Given props will be passed to the `Input` component, unless they are not relevant.
 * Reference the documentation there for details.
 *
 * @param props The properties for this component:
 * - prompt: The text to display as the label for the input field.
 * - includeButton: Whether to show a button to the right of the input field which will submit the input.
 * This button will be disabled if the input is empty or property `hasError = true`
 * - buttonText: The text to display on the button.
 * - onSubmit: The function to call when the button is clicked.
 * This callback is also used as the `onSubmit` prop for the `Input` component.
 *
 * @return {JSX.Element} The labeled input element
 * @constructor
 */
const LabeledInput = (props) => {
  // Variables used only at this level, plus all to pass down to subcomponents
  const { prompt, includeButton, buttonText, children, ...other } = props;
  // Variables used here and in subcomponents
  const { value, hasError, onSubmit } = other;

  return (
    <OuterContainer>
      <PositionedLabel>{prompt}</PositionedLabel>
      {includeButton ? (
        <InnerContainer width={"100%"}>
          <Input {...other}>{children}</Input>
          <Button isDisabled={!value || hasError} onClick={onSubmit}>
            {buttonText}
          </Button>
        </InnerContainer>
      ) : (
        <Input {...other}>{children}</Input>
      )}
    </OuterContainer>
  );
};

export default LabeledInput;
